import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";

export default function ChangePassword() {
  const { user, changePassword } = useAuth();
  const nav = useNavigate();
  const forced = !!user?.must_change_password;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { setError("New passwords do not match."); return; }
    setLoading(true); setError("");
    try {
      await changePassword(current, next);
      toast.success("Password updated!");
      nav(user?.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || "Could not change password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12" data-testid="change-password-page">
      <div className="w-full max-w-md glass rounded-2xl p-8">
        <div className="flex justify-center mb-6"><Logo className="h-12" showText /></div>
        <div className="flex justify-center mb-2"><KeyRound className="h-8 w-8 text-gold" /></div>
        <h1 className="font-display text-2xl font-bold text-white text-center">Change your password</h1>
        {forced && (
          <p className="text-sm text-gold text-center mt-2" data-testid="forced-change-notice">
            You signed in with a temporary password. Please set a new password to continue.
          </p>
        )}
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-slate-300">Current password</Label>
            <Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="change-current-input"
              className="bg-surface-2 border-white/10 text-white mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">New password</Label>
            <Input type="password" required minLength={6} value={next} onChange={(e) => setNext(e.target.value)} data-testid="change-new-input"
              className="bg-surface-2 border-white/10 text-white mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">Confirm new password</Label>
            <Input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="change-confirm-input"
              className="bg-surface-2 border-white/10 text-white mt-1" />
          </div>
          {error && <p className="text-sm text-rose-400" data-testid="change-error">{error}</p>}
          <Button type="submit" className="w-full btn-gold h-11" disabled={loading} data-testid="change-submit-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Update password
          </Button>
        </form>
      </div>
    </div>
  );
}
